import type { Project, CoCard } from './types';
import { getTxt } from './stepcommon';

/** 오늘 YYYY-MM-DD */
export function todayYmd(): string {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function parse(s: string): { y: number; m: number; d: number } | null {
  const m = (s || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? { y: +m[1], m: +m[2], d: +m[3] } : null;
}
function fmt(d: Date): string {
  return d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0') + '-' + String(d.getUTCDate()).padStart(2, '0');
}
/** 발행일로부터 N주년의 전날 (샘플 방식) */
function annivMinus1(issue: string, yrs: number): string {
  const p = parse(issue);
  if (!p) return '';
  const d = new Date(Date.UTC(p.y + yrs, p.m - 1, p.d));
  d.setUTCDate(d.getUTCDate() - 1);
  return fmt(d);
}
/** 만기일 = 발행 5주년 전날 */
export function matDate(issue: string): string { return annivMinus1(issue, 5); }
/** 전환청구 시작 = 발행 3주년 전날 */
export function convStart(issue: string): string { return annivMinus1(issue, 3); }
/** 전환청구 종료 = (발행+5년)의 전전달 말일 */
export function convEnd(issue: string): string {
  const p = parse(issue);
  if (!p) return '';
  return fmt(new Date(Date.UTC(p.y + 5, p.m - 2, 0)));
}
/** 기준일 = 발행 전년도 말일 (자본금·순재산액 공통) */
export function baseDate(issue: string): string {
  const p = parse(issue);
  return p ? (p.y - 1) + '-12-31' : '';
}

/** 숫자 문자열 → 천단위 콤마 (빈값/0 그대로) */
export function fmtComma(s: string): string {
  const d = onlyDigits(s);
  if (!d) return '';
  return Number(d).toLocaleString('ko-KR');
}
/** 콤마·기타 문자 제거 → 순수 숫자 문자열 */
export function onlyDigits(s: string): string {
  return (s || '').replace(/[^0-9]/g, '');
}

/** 사채 입력 필드 정의 (폼 + 문서생성 공용) */
export interface BondField { key: string; label: string; kind: 'text' | 'num' | 'date'; openDoc?: 'fin' | 'reg' | 'biz'; hint?: string; }
export const BOND_FIELDS: BondField[] = [
  { key: 'round', label: '사채 회차 (제N회)', kind: 'num', hint: '예: 1' },
  { key: 'issuerAddr', label: '발행회사 주소', kind: 'text', openDoc: 'biz' },
  { key: 'issuerCeo', label: '발행회사 대표이사', kind: 'text', openDoc: 'biz' },
  { key: 'baseDate', label: '재무 기준일 (전년도 말일)', kind: 'date' },
  { key: 'capital', label: '자본금+준비금 총액 (원)', kind: 'num', openDoc: 'fin' },
  { key: 'netAsset', label: '순재산액 (대차대조표상, 원)', kind: 'num', openDoc: 'fin' },
  { key: 'parValue', label: '1주 액면가 (원)', kind: 'num', openDoc: 'reg' },
  { key: 'convPrice', label: '전환가격 (1주당, 원)', kind: 'num' },
  { key: 'issueDate', label: '사채 발행일', kind: 'date' },
  { key: 'matDate', label: '만기일 (발행 5주년 전날)', kind: 'date' },
  { key: 'convStart', label: '전환청구 시작 (발행 3주년 전날)', kind: 'date' },
  { key: 'convEnd', label: '전환청구 종료 ((발행+5년) 전전달 말일)', kind: 'date' },
  { key: 'relation', label: '특수관계 내용 (동의서)', kind: 'text', hint: '투자대상회사와 조합원의 관계' },
];

/** 저장값 우선, 없으면 계산 디폴트 */
export function bondVal(p: Project, co: CoCard, key: string): string {
  const stored = getTxt(p, 's8', co.id + '_b_' + key);
  if (stored) return stored;
  const issue = getTxt(p, 's8', co.id + '_b_issueDate') || todayYmd();
  switch (key) {
    case 'issueDate': return todayYmd();
    case 'matDate': return matDate(issue);
    case 'convStart': return convStart(issue);
    case 'convEnd': return convEnd(issue);
    case 'baseDate': return baseDate(issue);
    case 'parValue': return '10000';
    case 'convPrice': return getTxt(p, 's8', co.id + '_b_parValue') || '10000';
    default: return '';
  }
}
